import React from 'react';
import { CheckCircle, AlertCircle, ArrowRight, ArrowLeft, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useWorkspaceStore from '../../../../../../store/workspace/workspaceStore';

interface StepProps {
  result: {
    ui_step: string;
    signal_id?: string;
    name?: string;
    success: boolean;
    error?: string;
    action?: 'created' | 'updated';
  };
  onSelect: (value: string, label: string) => void;
  onCreateAnother?: () => void;
  onGoBack?: () => void;
}

const SignalCreatedStep: React.FC<StepProps> = ({ result, onSelect, onCreateAnother, onGoBack }) => {
  const navigate = useNavigate();
  const workspace = useWorkspaceStore((s) => s.workspace);

  const handleViewSignals = () => {
    if (onGoBack) {
      onGoBack();
      return;
    }

    navigate(`/home/${workspace?.unique_identifier}/signals`);
  };

  const handleTryAgain = () => {
    onSelect('retry', 'Let me try building the signal again');
  };

  if (!result.success) {
    return (
      <div className="w-full max-w-lg p-4 rounded-lg border border-red-200 bg-red-50">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700">
              {result.action === 'updated' ? 'Failed to update signal' : 'Failed to create signal'}
            </p>
            {result.error && (
              <p className="text-xs text-red-500 mt-1">{result.error}</p>
            )}
            <button
              type="button"
              onClick={handleTryAgain}
              className="mt-3 text-xs font-medium text-red-600 hover:text-red-800 transition-colors underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg p-4 rounded-lg border border-green-200 bg-green-50">
      <div className="flex items-start gap-3">
        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-green-700">
            {result.action === 'updated' ? 'Signal updated!' : 'Signal created!'}
          </p>
          {result.name && (
            <p className="text-xs text-green-600 mt-0.5">
              <span className="font-medium">{result.name}</span> is now active.
            </p>
          )}
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={handleViewSignals}
              className="flex items-center gap-1.5 rounded-md border border-green-300 bg-white px-3 py-1.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-50"
            >
              View all signals
              <ArrowRight className="h-3 w-3" />
            </button>
            {onCreateAnother && (
              <button
                type="button"
                onClick={onCreateAnother}
                className="flex items-center gap-1.5 rounded-md border border-green-300 bg-white px-3 py-1.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-50"
              >
                <Plus className="h-3 w-3" />
                Create another signal
              </button>
            )}
            {onGoBack && (
              <button
                type="button"
                onClick={onGoBack}
                className="flex items-center gap-1.5 text-xs font-medium text-green-600 transition-colors hover:text-green-800"
              >
                <ArrowLeft className="h-3 w-3" />
                Go back
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignalCreatedStep;
